import {Button, Modal, Slider} from 'antd'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import Cropper from 'react-easy-crop'
// CSS của react-easy-crop được copy vào tailwind_custom.css (package đặt sideEffects:false
// nên import trực tiếp file .css dễ bị tree-shake mất khi build production).
import {BiReset, BiTrash} from 'react-icons/bi'
import {FiZoomIn, FiZoomOut} from 'react-icons/fi'

// ─── Kích thước cố định theo từng trường hợp dùng ───────────────────────────────
// caller chỉ gắn cropPurpose="...", KHÔNG cần biết con số. Output luôn ép đúng
// width×height (JPEG, nền trắng cho vùng trong suốt).
// defaultShape: khung crop (round chỉ là mask hiển thị, file xuất vẫn theo width×height);
// caller ghi đè bằng prop cropShape='round'|'rect'.
export const CROP_PURPOSES = {
    avatar: {width: 256, height: 256, defaultShape: 'round', labelKey: 'crop size avatar label'},
    course_cover: {width: 1200, height: 400, defaultShape: 'rect', labelKey: 'crop size course cover label'},
    certificate: {width: 1200, height: 900, defaultShape: 'rect', labelKey: 'crop size certificate label'},
    cover: {width: 1200, height: 400, defaultShape: 'rect', labelKey: 'crop size cover label'},
    announcement: {width: 400, height: 300, defaultShape: 'rect', labelKey: 'crop size announcement label'},
    exam_photo: {width: 1200, height: 400, defaultShape: 'rect', labelKey: 'crop size exam photo label'},
    portrait: {width: 600, height: 800, defaultShape: 'rect', labelKey: 'crop size portrait label'},
    banner_strip: {width: 1280, height: 256, defaultShape: 'rect', labelKey: 'crop size banner strip label'},
}

export const cropPurposeSize = (purpose) => {
    const p = CROP_PURPOSES[purpose]
    return p ? `${p.width} × ${p.height} px` : null
}

const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })

// Crop vùng đã chọn -> scale đúng target -> JPEG (nền trắng cho vùng trong suốt).
export const getCroppedImg = async (imageSrc, pixelCrop, targetW, targetH) => {
    const image = await loadImage(imageSrc)
    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, targetW, targetH)
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        targetW,
        targetH
    )
    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve({blob, base64: reader.result})
                reader.readAsDataURL(blob)
            },
            'image/jpeg',
            0.92
        )
    })
}

const ImageCropModal = ({open, file, purpose = 'course_cover', cropShape, onOk, onCancel, onRemove}) => {
    const t = useTranslate()
    const cfg = CROP_PURPOSES[purpose] || CROP_PURPOSES.course_cover
    const aspect = cfg.width / cfg.height
    // Hình dạng khung cố định: prop cropShape ghi đè, không thì theo cfg. Không cho đổi trong modal.
    const shape = cropShape === 'round' || cropShape === 'rect' ? cropShape : cfg.defaultShape || 'rect'

    const [srcFile, setSrcFile] = useState(file)
    const [imageSrc, setImageSrc] = useState(null)
    const [crop, setCrop] = useState({x: 0, y: 0})
    const [zoom, setZoom] = useState(1)
    const [areaPixels, setAreaPixels] = useState(null)
    const [mediaSize, setMediaSize] = useState(null)
    const [saving, setSaving] = useState(false)
    const replaceInputRef = useRef(null)

    useEffect(() => {
        setSrcFile(file)
    }, [file])

    useEffect(() => {
        let alive = true
        if (!srcFile) return undefined
        setCrop({x: 0, y: 0})
        setZoom(1)
        setMediaSize(null)
        readFileAsDataURL(srcFile)
            .then((url) => {
                if (alive) setImageSrc(url)
            })
            .catch(() => {
                if (alive) setImageSrc(null)
            })
        return () => {
            alive = false
        }
    }, [srcFile])

    const onCropComplete = useCallback((_area, pixels) => setAreaPixels(pixels), [])

    const reset = () => {
        setCrop({x: 0, y: 0})
        setZoom(1)
    }

    const pickReplace = () => replaceInputRef.current && replaceInputRef.current.click()
    const onReplaceChange = (e) => {
        const f = e.target.files && e.target.files[0]
        e.target.value = ''
        if (f) setSrcFile(f)
    }

    const handleOk = async () => {
        if (!areaPixels || !imageSrc) return
        setSaving(true)
        try {
            const result = await getCroppedImg(imageSrc, areaPixels, cfg.width, cfg.height)
            onOk(result)
        } finally {
            setSaving(false)
        }
    }

    const fileName = srcFile && srcFile.name
    const isSmallSource =
        mediaSize && (mediaSize.naturalWidth < cfg.width || mediaSize.naturalHeight < cfg.height)
    const sizeText = `${cfg.width} × ${cfg.height} px`

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            width={720}
            destroyOnHidden
            maskClosable={false}
            className="custom-modal"
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('edit image')}
                </div>
            }
            footer={[
                <Button
                    key="cancel"
                    onClick={onCancel}
                    className="text-color-200 button-rounded-lg no-border">
                    {t('cancel')}
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    loading={saving}
                    disabled={!areaPixels}
                    onClick={handleOk}
                    className="button-rounded-lg">
                    {t('save and apply image')}
                </Button>,
            ]}>
            <div className="rounded-xl border border-color-100 overflow-hidden">
                {/* Thanh trên: badge kích thước + tên file · nút Đổi ảnh / Xóa */}
                <div className="flex items-center justify-between gap-2 px-3 py-2 background-50 border-b border-color-100">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="flex items-center px-2 py-0.5 rounded-full bg-green-500 text-2xs font-semibold text-white whitespace-no-wrap flex-shrink-0">
                            {sizeText}
                        </span>
                        {fileName && <span className="font-medium text-xs text-color-100 truncate">{fileName}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={pickReplace}
                            className="px-2 py-1 flex items-center gap-1 rounded-lg background border border-color-100 hover:border-blue-400 text-color-100 hover:text-blue-600">
                            <BiReset size={12}/>
                            {t('change photo')}
                        </button>
                        <button
                            type="button"
                            onClick={onRemove}
                            className="px-2 py-1 flex items-center gap-1 rounded-lg bg-primary-50 hover:bg-primary-100 text-xs font-medium text-primary-500">
                            <BiTrash size={12}/>
                            {t('delete')}
                        </button>
                        <input
                            ref={replaceInputRef}
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp"
                            className="hidden"
                            onChange={onReplaceChange}
                        />
                    </div>
                </div>

                {/* Khung crop: ảnh kéo/zoom dưới khung cố định + lưới rule-of-thirds */}
                <div className="relative background-200" style={{height: 340, touchAction: 'none'}}>
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            cropShape={shape}
                            showGrid
                            minZoom={1}
                            maxZoom={3}
                            zoomSpeed={0.15}
                            restrictPosition
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                            onMediaLoaded={setMediaSize}
                        />
                    )}
                </div>

                {/* Điều khiển: zoom + Đặt lại + cảnh báo */}
                <div className="flex flex-col gap-1 px-3 py-2.5 background-50 border-t border-color-100">
                    <span className="text-xs font-medium text-color-200 tracking-wider">
                        {t('crop drag hint')}
                    </span>
                    <div className="flex items-center gap-3">
                        <FiZoomOut size={16} className="text-color-300 flex-shrink-0"/>
                        <Slider
                            className="flex-1"
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            tooltip={{formatter: (v) => `${Math.round(v * 100)}%`}}
                            onChange={setZoom}
                        />
                        <FiZoomIn size={16} className="text-color-300 flex-shrink-0"/>
                        <button
                            type="button"
                            onClick={reset}
                            className="px-2 py-1 flex items-center gap-1 rounded-lg background border border-color-100 hover:border-blue-400 text-color-100 hover:text-blue-600">
                            <BiReset size={12}/>
                            <span className="font-medium text-xs flex-shrink-0">
                                {t('crop reset')}
                            </span>
                        </button>
                    </div>

                    {isSmallSource && (
                        <span className="text-2xs text-amber-600 tracking-wide">
                            {`* ${t('crop source too small warning')}`}
                        </span>
                    )}
                </div>
            </div>
        </Modal>
    )
}

export default ImageCropModal
